<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Calendar from 'primevue/calendar';
import Column from 'primevue/column';
import DataTable, { DataTablePageEvent } from 'primevue/datatable';
import { can, useDateTime, useRouting } from '@/composables';
import { usePayrollReportStore, useReportStore } from '@/store';
import { DateFilterInterface, OrganizationDocumentsFilterInterface } from '@/types';
import { PERMISSIONS } from '@/enum';

const startDate = ref();
const endDate = ref();
const defaultFilters: OrganizationDocumentsFilterInterface = {
  page: 1,
  start_at: '',
  end_at: '',
  per_page: 10,
};

const $routing = useRouting();
const payrollReportStore = usePayrollReportStore();
const reportStore = useReportStore();
const filters = ref<OrganizationDocumentsFilterInterface>(defaultFilters);
const baseDates = ref(useDateTime().baseDates());

onMounted(() => init());

const isPayrollReport = computed<boolean>(() => $routing.route.name === 'reports.payroll');

const init = async () => {
  if (isPayrollReport.value) {
    await setDates();
    await defineFilters();
    await payrollReportStore.getPayroll(payload.value);
  }
};

$routing.routeChangeWatcher(async () => {
  if (isPayrollReport.value) {
    if (!datesInverted.value) await payrollReportStore.getPayroll(payload.value);
    await defineFilters();
    await reportStore.getReportServiceProviderFilter(filters.value.start_at, filters.value.end_at);
  }
});

const onStartDateRangeChange = async (dateStart: Date) => {
  if (!datesInverted.value) {
    const start_at = useDateTime().formatDateForApi(dateStart);
    const end = new Date(endDate.value.getFullYear(), endDate.value.getMonth() + 1, 0);
    const end_at = useDateTime().formatDateForApi(end);

    await reportStore.getReportServiceProviderFilter(start_at, end_at);

    $routing.addQueryParams({ start_at, end_at });
  }
};

const onEndDateRangeChange = async (dateEnd: Date) => {
  const end = new Date(dateEnd.getFullYear(), dateEnd.getMonth() + 1, 0);
  endDate.value = end;
  if (!datesInverted.value) {
    const start_at = useDateTime().formatDateForApi(startDate.value);
    const end_at = useDateTime().formatDateForApi(end);
    $routing.addQueryParams({ start_at, end_at });
  }
};

/**
 * set up the dates
 */
const setDates = async () => {
  startDate.value = baseDates.value[0];
  endDate.value = baseDates.value[1];
  const query = $routing.route.query;

  //if there is no query_params for start and end dates add them
  if (!query.end_at && !query.start_at) {
    $routing.addQueryParams({
      start_at: useDateTime().formatDateForApi(startDate.value),
      end_at: useDateTime().formatDateForApi(endDate.value),
      per_page: defaultFilters.per_page,
      page: defaultFilters.page,
    });
  }
};

/**
 * add payload instead of route params for calls to the API
 * TODO: add organization array and selected clinicians
 */
const payload = computed<DateFilterInterface>(() => {
  const end = new Date(endDate.value.getFullYear(), endDate.value.getMonth() + 1, 0);
  return {
    start_at: useDateTime().formatDateForApi(startDate.value),
    end_at: useDateTime().formatDateForApi(end),
    per_page: filters.value.per_page,
    page: filters.value.page,
  };
});

/**
 * Define the filters based on the query params available on page load.
 */
const defineFilters = async () => {
  const query = $routing.route.query;

  filters.value = {
    start_at: query.start_at ? (query.start_at as string) : '',
    end_at: query.end_at ? (query.end_at as string) : '',
    page: query.page ? parseInt(query.page as string) : 1,
    per_page: query.per_page ? parseInt(query.per_page as string) : defaultFilters.per_page,
  };
};

/**
 * Check if the user is allowed to download the report based on the resultset totals and the limit.
 */
const canDownload = computed<boolean>(
  () =>
    payrollReportStore.payroll.total < payrollReportStore.payroll.limit && can([PERMISSIONS.REPORTS_PAYMENT_DOWNLOAD]),
);

/**
 * Handle the pagination of the request data.
 */
const onPage = (event: DataTablePageEvent) => {
  filters.value.page = event.page + 1;
  filters.value.per_page = event.rows;
  $routing.addQueryParams({ page: event.page + 1, per_page: event.rows });
};

/**
 * Handle the export action for the specific report type.
 */
const onExportReport = (type: string) => {
  if (canDownload.value) {
    payrollReportStore.downloadPayrollReport($routing.route.query, type);
  } else {
    payrollReportStore.doToast(
      'You will receive an email with a link to download the exported data.',
      'Your export has started',
    );
  }
};

/**
 * Define the tooltip text for the export buttons.
 */
const tooltipExportText = (type: string) => (canDownload.value ? `Download as ${type}` : `Email as ${type}`);

const datesInverted = computed(() => {
  return startDate.value && endDate.value ? startDate.value.valueOf() >= endDate.value.valueOf() : false;
});
</script>

<template>
  <div class="page-content">
    <Card>
      <template #content>
        <DataTable
          v-model:filters="filters"
          filter-display="menu"
          :value="payrollReportStore.payroll.data"
          :paginator="true"
          :rows="filters.per_page"
          :total-records="payrollReportStore.payroll.total"
          data-key="id"
          :row-hover="true"
          :lazy="true"
          loading-icon="logo-spinner"
          :loading="payrollReportStore.payroll.loading"
          :rows-per-page-options="[10, 20, 50, 100]"
          paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          current-page-report-template="Showing {first} to {last} of {totalRecords}"
          responsive-layout="scroll"
          @page="onPage"
        >
          <template #header>
            <div class="flex grid p-3 justify-content-between">
              <div class="flex flex-row gap-1 align-items-center justify-content-start font-bold text-sm">
                <Calendar
                  v-model="startDate"
                  view="month"
                  class="w-full text-sm"
                  date-format="MM yy"
                  @date-select="onStartDateRangeChange"
                />
                to
                <Calendar
                  v-model="endDate"
                  view="month"
                  class="w-full text-sm"
                  date-format="MM yy"
                  @date-select="onEndDateRangeChange"
                />
              </div>
              <div class="justify-content-end flex flex-row">
                <div class="col-fixed">
                  <AppButton
                    v-tooltip.bottom="tooltipExportText('CSV')"
                    type="secondary"
                    text="CSV"
                    icon-left="system_update_alt"
                    @click="onExportReport('csv')"
                  />
                </div>
                <div class="col-fixed">
                  <AppButton
                    v-tooltip.bottom="tooltipExportText('XLSX')"
                    type="secondary"
                    text="XLSX"
                    icon-left="system_update_alt"
                    @click="onExportReport('xlsx')"
                  />
                </div>
              </div>
            </div>
          </template>
          <template #empty>No Payroll Found</template>
          <template #loading>Loading data. Please wait.</template>
          <template v-if="payrollReportStore.showAllColumns">
            <Column field="external_id" header="ID" body-class="text-center" />
            <Column field="bucket_start_at" header="Start Date" body-class="text-center" header-class="text-center">
              <template #body="{ data }">{{ useDateTime().formatIntlDate(data.bucket_start_at) }}</template>
            </Column>
            <Column field="bucket_end_at" header="End Date" body-class="text-center" header-class="text-center">
              <template #body="{ data }">{{ useDateTime().formatIntlDate(data.bucket_end_at) }}</template>
            </Column>
            <Column field="last_name" header="Last Name" body-class="text-center" header-class="text-center" />
            <Column field="first_name" header="First Name" body-class="text-center" header-class="text-center" />
            <Column field="organization_name" header="Organizations" body-class="text-center" />
            <Column field="quote_id" header="QuoteID" body-class="text-center" />
            <Column
              field="payment_type"
              header="Compensation Type"
              body-class="text-center"
              header-class="text-center"
            />
            <Column field="rate" header="Rate" body-class="text-center">
              <template #body="{ data }">${{ data.rate }}</template>
            </Column>
            <Column field="doc_hour" header="Doc Hour" body-class="text-center" />
            <Column field="sow_hours" header="SOW Hours" body-class="text-center" />
            <Column field="total" header="Total" body-class="text-center">
              <template #body="{ data }">${{ data.total }}</template>
            </Column>
          </template>
        </DataTable>
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  .p-datatable .p-column-header-content {
    justify-content: center;

    .p-datatable-header {
      background: $white;
      padding: 0.5rem;
    }
  }
  .p-datatable-wrapper {
    margin: 0px !important;
  }

  .p-paginator-current {
    flex: 1;
    justify-content: flex-end;
  }
}

.text-red-600 {
  color: $red;
}
</style>
